import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'
import permanentDstPlugin from './dayjs-permanent-dst'
import {
  getBrowserTimezone,
  getTimezoneAbbreviation,
  resolveEffectiveTimezone,
} from './timezone-display'

// Helpers here (`setTimeToBeDefined` / `isTimeToBeDefined` and the timezone
// formatters) call `.utc()` / `.tz()`. Registering the plugins here (the house
// pattern — see `ts-utils/registration-datetime.ts`) rather than relying on some
// other module in the bundle having done it first, so this file works
// standalone: in a unit test, on mobile, anywhere.
dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)
// Keeps zones that abolished their clock change reading correctly on a runtime
// whose timezone database still has the old rule — see dayjs-permanent-dst.
dayjs.extend(permanentDstPlugin)

/**
 * Formats a game date into uppercase format
 * Example output: "JAN 1 - 11:59 AM"
 * @param gameDate - dayjs date object to format
 * @returns Formatted date string in uppercase
 */
export const formatGameDate = (gameDate: dayjs.Dayjs): string => {
  const formattedDate: string = gameDate.format('MMM D - h:mm A').toUpperCase()
  return formattedDate
}

/**
 * Formats a date string to display in a long format with ordinal day
 * Example output: "January 1, 2024 11:59 AM"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatLongDateTime = (dateString: string): string => {
  return dayjs(dateString).utc().format('MMMM D, YYYY h:mm A')
}

/**
 * Formats a date string to display in a short format with time
 * Example output: "Jan 1, 2024 11:59 AM"
 * @param dateString - ISO date string to format
 * @param useUtc - Whether to use UTC timezone (default: true)
 * @param timezone - Optional timezone to convert to (e.g. 'America/New_York')
 * @returns Formatted date string
 */
export const formatShortDateTime = (
  dateString: string,
  useUtc: boolean = true,
  timezone?: string
): string => {
  let date = useUtc ? dayjs(dateString).utc() : dayjs(dateString)
  if (timezone) {
    date = date.tz(timezone)
  }
  return date.format('MMM D, YYYY h:mm A')
}

/**
 * Formats a date string to display in a long format with ordinal day
 * Example output: "January 1, 2024"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatLongDate = (dateString: string): string => {
  return dayjs(dateString).utc().format('MMMM D, YYYY')
}

/**
 * Formats a date string to display in a short format
 * Example output: "Jan 1, 2024"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatShortDate = (
  dateString: string,
  useUtc: boolean = true
): string => {
  const date = useUtc ? dayjs(dateString).utc() : dayjs(dateString)
  return date.format('MMM D, YYYY')
}

/**
 * An instant as the viewer reads it on their own clock (their browser's zone), in
 * `pattern` — "MMM D" for "Oct 1", "h:mm A" for "2:15 PM". The zone is named, so
 * the permanent-DST plugin corrects it. '' for an empty or unparseable value.
 */
export const formatInViewerZone = (
  dateString: string | null | undefined,
  pattern: string
): string => {
  if (!dateString) {
    return ''
  }
  const date = dayjs(dateString)
  if (!date.isValid()) {
    return ''
  }
  const zone = getBrowserTimezone()

  return (zone ? date.tz(zone) : date).format(pattern)
}

/**
 * Formats a date-only value (stored as UTC midnight) as month + year.
 * Example output: "Apr 2026"
 *
 * Read in UTC on purpose: the value is a calendar date, so the viewer's zone
 * must not shift `2026-07-01T00:00:00Z` back into June.
 *
 * @param dateString - ISO date string (UTC midnight)
 * @returns formatted string, or '' for empty/unparseable input
 */
export const formatMonthYear = (
  dateString: string | null | undefined
): string => {
  if (!dateString) {
    return ''
  }
  const date = dayjs(dateString).utc()
  return date.isValid() ? date.format('MMM YYYY') : ''
}

/**
 * Formats a date-only value (a birth date, a suspension day) as `YYYY-MM-DD`.
 * Example output: "2008-08-26"
 *
 * Parsed as UTC: it's a calendar date, so the viewer's zone must not move it
 * to the day before.
 *
 * @param dateString - date or ISO date string
 * @returns formatted string, or '' for empty/unparseable input
 */
export const formatCalendarDate = (
  dateString: string | null | undefined
): string => {
  if (!dateString) {
    return ''
  }
  const date = dayjs.utc(dateString)
  return date.isValid() ? date.format('YYYY-MM-DD') : ''
}

/**
 * Formats a date-only value as month + day, without the year.
 * Example output: "Aug 26"
 *
 * Parsed as UTC for the same reason as `formatCalendarDate`.
 *
 * @param dateString - date or ISO date string
 * @returns formatted string, or '' for empty/unparseable input
 */
export const formatCalendarMonthDay = (
  dateString: string | null | undefined
): string => {
  if (!dateString) {
    return ''
  }
  const date = dayjs.utc(dateString)
  return date.isValid() ? date.format('MMM D') : ''
}

/**
 * Formats a span of date-only values (stored as UTC midnight).
 * Example output: "Jul 30 – Sep 30, 2026", or "Dec 1, 2026 – Feb 1, 2027"
 * across a year. A missing end gives the start alone.
 *
 * Read in UTC for the same reason as `formatMonthYear`: they are calendar
 * dates, not instants.
 *
 * @param startString - ISO date string (UTC midnight)
 * @param endString - ISO date string (UTC midnight)
 * @returns formatted string, or '' when the start is empty/unparseable
 */
export const formatDateRange = (
  startString: string | null | undefined,
  endString: string | null | undefined
): string => {
  const start = startString ? dayjs(startString).utc() : null
  if (!start?.isValid()) {
    return ''
  }
  const end = endString ? dayjs(endString).utc() : null
  if (!end?.isValid()) {
    return start.format('MMM D, YYYY')
  }
  const startFormat = start.year() === end.year() ? 'MMM D' : 'MMM D, YYYY'
  return `${start.format(startFormat)} – ${end.format('MMM D, YYYY')}`
}

/**
 * Formats a stored UTC timestamp as date + time + timezone abbreviation.
 * Example output: "Jul 16, 2026 4:25 PM PDT"
 *
 * Rendered in `timezone` when given, otherwise the viewer's own zone — never
 * UTC, which would show a time nobody recognizes. The abbreviation is resolved
 * at that instant, so it correctly reads PDT in summer and PST in winter.
 *
 * @param dateString - ISO date string (UTC) to format
 * @param timezone - optional IANA timezone (e.g. 'America/New_York')
 * @returns formatted string, or '' for empty/unparseable input
 */
export const formatDateTimeWithZone = (
  dateString: string | null | undefined,
  timezone?: string | null
): string => {
  if (!dateString) {
    return ''
  }

  const asUtc = dayjs.utc(dateString)
  if (!asUtc.isValid()) {
    return ''
  }

  const effectiveTimezone = resolveEffectiveTimezone(timezone)
  const zoned = effectiveTimezone ? asUtc.tz(effectiveTimezone) : asUtc
  const abbreviation = effectiveTimezone
    ? getTimezoneAbbreviation(effectiveTimezone, asUtc.toDate())
    : 'UTC'

  const formatted = zoned.format('MMM D, YYYY h:mm A')
  return abbreviation ? `${formatted} ${abbreviation}` : formatted
}

/**
 * Formats a stored UTC timestamp as a short calendar date in a zone.
 * Example output: "Jul 16, 2026"
 *
 * Rendered in `timezone` when given, otherwise the viewer's own zone — never
 * UTC, so a late-evening instant lands on the day the viewer recognizes.
 *
 * @param dateString - ISO date string (UTC) to format
 * @param timezone - optional IANA timezone (e.g. 'America/New_York')
 * @returns formatted string, or '' for empty/unparseable input
 */
export const formatShortDateInZone = (
  dateString: string | null | undefined,
  timezone?: string | null
): string => {
  if (!dateString) {
    return ''
  }

  const asUtc = dayjs.utc(dateString)
  if (!asUtc.isValid()) {
    return ''
  }

  const effectiveTimezone = resolveEffectiveTimezone(timezone)
  const zoned = effectiveTimezone ? asUtc.tz(effectiveTimezone) : asUtc
  return zoned.format('MMM D, YYYY')
}

/**
 * Returns a date string in ISO format with the time set to 23:58:58 UTC,
 * This is used to indicate that the game time is to be defined.
 * @param isoDateString - ISO date string (e.g., from locationStartDate.toUTC().toISO())
 * @returns ISO date string with time set to 23:58:58
 */
export const setTimeToBeDefined = (isoDateString: string): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }
  return dayjs(isoDateString)
    .utc()
    .set('hour', 23)
    .set('minute', 58)
    .set('second', 58)
    .set('millisecond', 0)
    .toISOString()
}

/**
 * Returns a date string in ISO format with the time set to 23:58:58 UTC,
 * This is used to indicate that the game time is to be defined.
 * @param isoDateString - ISO date string (e.g., from locationStartDate.toUTC().toISO())
 * @returns ISO date string with time set to 23:58:58
 */
export const setTimeToBeDefinedNoISO = (isoDateString: string): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }
  return dayjs(isoDateString)
    .utc()
    .set('hour', 23)
    .set('minute', 58)
    .set('second', 58)
    .set('millisecond', 0)
    .toString()
}

/**
 * Clears the "to be defined" sentinel time from a date, keeping the calendar
 * date but setting the time-of-day to the current time. Used when a user
 * toggles TBD off, so the picker pre-selects "now" instead of 23:58:58.
 * @param isoDateString - ISO date string holding the TBD sentinel time
 * @param timezone - IANA timezone the date should be interpreted in (e.g. the
 * venue's). Falls back to local time when omitted.
 * @returns ISO date string on the same date with the current time-of-day
 */
export const clearTimeToBeDefined = (
  isoDateString: string,
  timezone?: string | null
): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }

  const zoned = timezone
    ? dayjs(isoDateString).tz(timezone)
    : dayjs(isoDateString)
  const now = timezone ? dayjs().tz(timezone) : dayjs()

  return zoned
    .set('hour', now.hour())
    .set('minute', now.minute())
    .set('second', now.second())
    .set('millisecond', 0)
    .toISOString()
}

/**
 * Checks if the given ISO date string has the time set to 23:58:58 UTC,
 * which indicates that the game time is to be defined.
 * @param isoDateString - ISO date string to check
 * @returns True if the time is set to 23:58:58 UTC, false otherwise
 */
export const isTimeToBeDefined = (isoDateString: string): boolean => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return false
  }
  const date = dayjs(isoDateString).utc()
  return (
    date.hour() === 23 &&
    date.minute() === 58 &&
    date.second() === 58 &&
    date.millisecond() === 0
  )
}

/**
 * SN-8309 — the calendar date an instant falls on at the VENUE, as YYYY-MM-DD:
 * what the Player Finder sends as a game's `date`, so the availability-window
 * match uses the game's own weekday. A late-evening game is already the next
 * day in UTC. A missing or unknown zone reads the instant in UTC — the same
 * fallback the API applies — never the viewer's device zone.
 */
export const toVenueLocalDate = (
  startsAt?: string | null,
  timezone?: string | null
): string | undefined => {
  if (!startsAt) {
    return undefined
  }
  const instant = dayjs.utc(startsAt)
  if (!instant.isValid()) {
    return undefined
  }
  if (timezone) {
    try {
      return instant.tz(timezone).format('YYYY-MM-DD')
    } catch {
      // Not a zone this runtime knows — fall back to UTC, like the API.
    }
  }
  return instant.format('YYYY-MM-DD')
}
